import { defineComponent as _defineComponent } from 'vue'
import { useContext } from '@nuxtjs/composition-api';
import { useIntervalFn } from '@vueuse/core';
import { onMounted, ref, watch } from 'vue';
import isQualified from '~/queries/isQualified.gql';
import getRestrictionEventCount from '~/queries/getRestrictionEventCount.gql';
import {
  isRestrictionSuccess,
  getFirstRestrictionSuccessResultByFilter,
} from '~/utils/parseRestrictionResults';
import { formatNumber } from '~/utils/format';
import { IMuxSuccessReasonDetails } from './VideoPlayer.vue';
import { VideoWatchEventTypes } from '~/types/event-restriction';


export default /*#__PURE__*/_defineComponent({
  __name: 'VideoStats',
  props: {
    slug: null,
    displayThreshold: null,
    isLive: { type: Boolean },
    playbackId: null,
    restrictionId: null,
    showLikes: { type: Boolean },
    showViews: { type: Boolean },
    statsToken: null,
    type: null
  },
  setup(__props: any) {

const props = __props as {
  slug?: string;
  displayThreshold?: number;
  isLive?: boolean;
  playbackId: string;
  restrictionId?: string;
  showLikes?: boolean;
  showViews?: boolean;
  statsToken?: string;
  type: string;
};



const {
  app: { apolloProvider },
} = useContext();

const statsToken = ref(props.statsToken);
const totalViews = ref(0);
const currentViews = ref(0);
const totalViewsInit = ref(false);
const currentViewsInit = ref(false);

const isRestricted = async () => {
  if (!apolloProvider?.defaultClient) {
    return;
  }
  try {
    const { data } = await apolloProvider.defaultClient.query({
      query: isQualified,
      variables: {
        id: props.restrictionId,
        payload: {
          playbackId: props.playbackId,
        },
      },
      fetchPolicy: 'network-only',
    });
    return data;
  } catch (e) {
    console.error(e);
  }
};

const getViews = async <TPayload>(
  restrictionId: string,
  payload: TPayload,
  filter: string,
): Promise<number> => {
  if (!restrictionId || !apolloProvider?.defaultClient) {
    return 0;
  }
  const bucket = `${props.type}|${props.slug}`;
  const { data } = await apolloProvider.defaultClient.query({
    query: getRestrictionEventCount,
    variables: {
      RestrictionEventParams: {
        type: VideoWatchEventTypes.VIDEO_WATCH_STARTED,
        bucket,
      },
    },
    fetchPolicy: 'network-only',
  });

  return data?.getRestrictionEventCount?.count ?? 0;
};

const getTotalViews = async () =>
  getViews<{ playbackId: string }>(
    process.env.muxTotalViewsRestriction ?? '',
    { playbackId: props.playbackId },
    'muxVideoTotalViews',
  );

const getCurrentViews = async () => {
  if (!statsToken.value) {
    return 0;
  }
  return getViews<{ playbackId: string; statsToken: string }>(
    process.env.muxCurrentViewsRestriction ?? '',
    { playbackId: props.playbackId, statsToken: statsToken.value },
    'muxCurrentViewsByToken',
  );
};

const {
  pause: pauseTotalViews,
  resume: resumeTotalViews,
  isActive: isTotalViewsIntervalActive,
} = useIntervalFn(
  async () => {
    totalViews.value = await getTotalViews();
    if (!totalViewsInit.value) {
      totalViewsInit.value = true;
    }
  },
  30000,
  { immediate: false, immediateCallback: true },
);

const {
  pause: pauseCurrentViews,
  resume: resumeCurrentViews,
  isActive: isCurrentViewsIntervalActive,
} = useIntervalFn(
  async () => {
    currentViews.value = await getCurrentViews();
    if (!currentViewsInit.value) {
      currentViewsInit.value = true;
    }
  },
  30000,
  { immediate: false, immediateCallback: true },
);

onMounted(async () => {
  // we only need stats token for live views
  if (
    !statsToken.value &&
    props.restrictionId &&
    props.isLive &&
    props.showViews
  ) {
    const data = await isRestricted();
    const isSuccess = isRestrictionSuccess(data.isQualified);
    if (isSuccess) {
      const result =
        getFirstRestrictionSuccessResultByFilter<IMuxSuccessReasonDetails>(
          data.isQualified,
          'isMuxPlaybackId',
        );
      statsToken.value = result?.statsToken;
    }
  }
  if (props.showViews && !isTotalViewsIntervalActive.value) {
    resumeTotalViews();
  }
});

watch(
  statsToken,
  (newToken, oldToken) => {
    if (!newToken || !props.isLive || !props.showViews) return;
    if (newToken === oldToken) return;
    if (!isCurrentViewsIntervalActive.value) {
      resumeCurrentViews();
    }
  },
  { immediate: true },
);

return { __sfc: true,props, apolloProvider, statsToken, totalViews, currentViews, totalViewsInit, currentViewsInit, isRestricted, getViews, getTotalViews, getCurrentViews, pauseTotalViews, resumeTotalViews, isTotalViewsIntervalActive, pauseCurrentViews, resumeCurrentViews, isCurrentViewsIntervalActive, formatNumber }
}

})